import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Trophy, Flame, Zap } from 'lucide-react';

interface ThrivePointsData {
  totalPoints: number;
  totalTransactions: number;
  byComponent: Array<{ name: string; value: number }> | null;
}

interface StreakData {
  totalStreaks: number;
  avgCurrentStreak: number | null;
  maxLongestStreak: number | null;
  byType: Array<{ name: string; value: number; avgStreak: number }> | null;
}

interface EngagementMetricsCardProps {
  thrivePoints: ThrivePointsData | null;
  streaks: StreakData | null;
  isLoading: boolean;
}

const COMPONENT_COLORS: Record<string, string> = {
  'WELLNESS': 'hsl(142, 76%, 36%)',
  'SAFETY': 'hsl(217, 91%, 60%)',
  'COMMUNITY': 'hsl(262, 83%, 58%)',
  'ASSESSMENTS': 'hsl(38, 92%, 50%)',
};

const STREAK_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export const EngagementMetricsCard: React.FC<EngagementMetricsCardProps> = ({
  thrivePoints,
  streaks,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Engagement Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          Engagement Metrics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* ThrivePoints Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Zap className="h-4 w-4 text-yellow-500" />
              ThrivePoints
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {thrivePoints?.totalPoints?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-muted-foreground">Total Points</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-foreground">
                  {thrivePoints?.totalTransactions?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-muted-foreground">Transactions</div>
              </div>
            </div>
            {thrivePoints?.byComponent && thrivePoints.byComponent.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">Points by Component</div>
                {thrivePoints.byComponent.map((item) => (
                  <div key={item.name} className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COMPONENT_COLORS[item.name] || 'hsl(var(--primary))' }}
                      />
                      {item.name}
                    </span>
                    <span className="font-medium">{item.value?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Streak Summary Stats */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Flame className="h-4 w-4 text-orange-500" />
              Streak Statistics
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Total Streaks</span>
                <span className="text-xl font-bold">{streaks?.totalStreaks || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Avg Current</span>
                <span className="text-xl font-bold">{streaks?.avgCurrentStreak || 0} days</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Max Streak</span>
                <span className="text-xl font-bold text-primary">{streaks?.maxLongestStreak || 0} days</span>
              </div>
            </div>
          </div>

          {/* Streaks by Type Chart */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Streaks by Type</div>
            {streaks?.byType && streaks.byType.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={streaks.byType} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={100} 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value.replace(/_/g, ' ')}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value, name === 'value' ? 'Count' : 'Avg Streak']}
                    labelFormatter={(label) => label.replace(/_/g, ' ')}
                  />
                  <Bar dataKey="value" name="Count">
                    {streaks.byType.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={STREAK_COLORS[index % STREAK_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-muted-foreground">
                No streak data
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
